import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Headphones, Volume2, Play, Pause, Download, Key, 
  AlertCircle, CheckCircle, Loader2, Info, Lock, 
  Globe, User, Baby, HelpCircle, DollarSign, Shield,
  BookOpen, List, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chapter Markers Component
const ChapterMarkersSection = ({ manuscript, onProcessedText }) => {
  const [chapters, setChapters] = useState([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pauseDuration, setPauseDuration] = useState('long');
  const [processedText, setProcessedText] = useState('');

  const detectChapters = async () => {
    if (!manuscript || manuscript.length < 100) return;
    
    setIsDetecting(true);
    try {
      const response = await axios.post(`${API}/audiobook/chapters/detect`, {
        text: manuscript
      });
      setChapters(response.data.chapters);
    } catch (err) {
      console.error('Error detecting chapters:', err);
    } finally {
      setIsDetecting(false);
    }
  };

  const addMarkers = async () => {
    if (!manuscript) return;
    
    setIsProcessing(true);
    try {
      const response = await axios.post(`${API}/audiobook/chapters/add-markers`, {
        text: manuscript,
        pause_duration: pauseDuration
      });
      setProcessedText(response.data.processed);
      if (onProcessedText) {
        onProcessedText(response.data.processed);
      }
    } catch (err) {
      console.error('Error adding markers:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (manuscript && manuscript.length > 100) {
      detectChapters();
    }
  }, [manuscript]);

  return (
    <div className="chapter-markers-section">
      <div className="section-header">
        <h4><List size={18} /> Chapter Markers</h4>
        <p className="section-description">
          Auto-detect chapters and add professional pause markers for a better audiobook experience.
        </p>
      </div>

      {chapters.length > 0 && (
        <div className="chapters-detected">
          <div className="chapters-summary">
            <CheckCircle size={16} />
            <span><strong>{chapters.length}</strong> chapters detected</span>
          </div>
          <div className="chapters-list">
            {chapters.slice(0, 5).map((ch, i) => (
              <div key={i} className="chapter-item">
                <span className="chapter-num">{ch.number}</span>
                <span className="chapter-title">{ch.title}</span>
                <span className="chapter-words">{ch.word_count.toLocaleString()} words</span>
              </div>
            ))}
            {chapters.length > 5 && (
              <div className="chapter-item more">
                <span>+ {chapters.length - 5} more chapters...</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="pause-settings">
        <label>Pause Duration Between Chapters</label>
        <div className="pause-options">
          <button 
            className={`pause-option ${pauseDuration === 'short' ? 'active' : ''}`}
            onClick={() => setPauseDuration('short')}
          >
            <Clock size={14} />
            <span>Short (1s)</span>
          </button>
          <button 
            className={`pause-option ${pauseDuration === 'medium' ? 'active' : ''}`}
            onClick={() => setPauseDuration('medium')}
          >
            <Clock size={14} />
            <span>Medium (2s)</span>
          </button>
          <button 
            className={`pause-option ${pauseDuration === 'long' ? 'active' : ''}`}
            onClick={() => setPauseDuration('long')}
          >
            <Clock size={14} />
            <span>Long (3s)</span>
          </button>
        </div>
      </div>

      <button 
        className="secondary-btn"
        onClick={addMarkers}
        disabled={isProcessing || !manuscript}
      >
        {isProcessing ? (
          <>
            <Loader2 className="spinning" size={16} />
            <span>Processing...</span>
          </>
        ) : (
          <>
            <BookOpen size={16} />
            <span>Add Chapter Markers</span>
          </>
        )}
      </button>

      {processedText && (
        <div className="markers-applied">
          <CheckCircle size={16} />
          <span>Chapter markers applied! Your audiobook will have natural pauses between chapters.</span>
        </div>
      )}
    </div>
  );
};

// Voice cards with descriptions
const VoiceCard = ({ voice, voiceData, selected, onSelect }) => {
  const genderIcons = {
    male: <User size={16} />,
    female: <User size={16} className="text-pink-400" />,
    neutral: <User size={16} className="text-purple-400" />
  };
  
  const ageLabels = {
    adult: "Adult",
    young_adult: "Young Adult",
    mature: "Mature",
    young: "Child-friendly"
  };

  return (
    <div 
      className={`voice-card ${selected ? 'selected' : ''}`}
      onClick={() => onSelect(voice)}
      data-testid={`voice-${voice}`}
    >
      <div className="voice-card-header">
        <h5>{voiceData.name}</h5>
        <span className="voice-type">{voiceData.type}</span>
      </div>
      <p className="voice-description">{voiceData.description}</p>
      <div className="voice-tags">
        <span className="voice-tag">{genderIcons[voiceData.gender]} {voiceData.gender}</span>
        <span className="voice-tag">{ageLabels[voiceData.age]}</span>
      </div>
    </div>
  );
};

// API Key Guide Modal
const ApiKeyGuideModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content api-guide-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3><Key size={24} /> What is an API Key?</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <section className="guide-section">
            <h4><HelpCircle size={18} /> Simple Explanation</h4>
            <p>
              Think of an API key like a <strong>special password</strong> that lets Scribe Flow 
              communicate with OpenAI's computers to convert your text to speech. It's your 
              personal pass that says "Yes, this person is allowed to use this service."
            </p>
            <div className="analogy-box">
              <strong>Real-World Analogy:</strong> It's like a membership card at a club - 
              it lets you in and tracks what services you use.
            </div>
          </section>

          <section className="guide-section">
            <h4><Globe size={18} /> Who Uses API Keys?</h4>
            <p>Millions of people and companies use API keys every day:</p>
            <ul>
              <li><strong>Uber</strong> uses Google Maps API for routes</li>
              <li><strong>Weather apps</strong> use weather APIs for forecasts</li>
              <li><strong>Instagram</strong> uses APIs to share to Facebook</li>
              <li>Almost <strong>every app</strong> on your phone uses APIs!</li>
            </ul>
          </section>

          <section className="guide-section">
            <h4><CheckCircle size={18} /> How to Get Your API Key (Step-by-Step)</h4>
            <ol className="steps-list">
              <li>
                <strong>Create an OpenAI Account</strong>
                <p>Go to <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a> and click "Sign Up"</p>
              </li>
              <li>
                <strong>Add Payment Method</strong>
                <p>Click your profile → Billing → Add payment method</p>
                <p className="cost-note">💡 Pay-as-you-go: You only pay for what you use!</p>
              </li>
              <li>
                <strong>Generate Your Key</strong>
                <p>Profile → API Keys → "Create new secret key"</p>
                <p className="warning-note">⚠️ Copy it immediately - it's only shown once!</p>
              </li>
              <li>
                <strong>Set Spending Limit (Recommended)</strong>
                <p>Billing → Limits → Set monthly budget (e.g., $10-20)</p>
              </li>
            </ol>
          </section>

          <section className="guide-section">
            <h4><DollarSign size={18} /> Cost Examples</h4>
            <div className="cost-grid">
              <div className="cost-item">
                <span>Short story (10k chars)</span>
                <span className="cost-value">$0.15 - $0.30</span>
              </div>
              <div className="cost-item">
                <span>Novella (50k chars)</span>
                <span className="cost-value">$0.75 - $1.50</span>
              </div>
              <div className="cost-item">
                <span>Novel (100k chars)</span>
                <span className="cost-value">$1.50 - $3.00</span>
              </div>
              <div className="cost-item">
                <span>Full book (250k chars)</span>
                <span className="cost-value">$3.75 - $7.50</span>
              </div>
            </div>
          </section>

          <section className="guide-section security-section">
            <h4><Shield size={18} /> Security: Keep Your Key Safe</h4>
            <div className="security-grid">
              <div className="do-section">
                <h5>✅ DO:</h5>
                <ul>
                  <li>Write it on paper, store with important docs</li>
                  <li>Treat it like a bank password</li>
                  <li>Set spending limits on OpenAI</li>
                  <li>Use only in trusted apps like Scribe Flow</li>
                </ul>
              </div>
              <div className="dont-section">
                <h5>❌ DON'T:</h5>
                <ul>
                  <li>Save in text files on computer</li>
                  <li>Share in emails or messages</li>
                  <li>Post anywhere online</li>
                  <li>Store in cloud documents</li>
                </ul>
              </div>
            </div>
            <div className="security-tip">
              <Lock size={16} />
              <span><strong>Why paper?</strong> Hackers can breach computers and cloud accounts, 
              but can't access a paper in your filing cabinet!</span>
            </div>
          </section>
        </div>
        <div className="modal-footer">
          <button className="primary-btn" onClick={onClose}>Got It!</button>
        </div>
      </div>
    </div>
  );
};

// Main Audiobook Component
const AudiobookSection = ({ manuscript, writingType }) => {
  const [voices, setVoices] = useState({});
  const [selectedVoice, setSelectedVoice] = useState('coral');
  const [selectedModel, setSelectedModel] = useState('tts-1-hd');
  const [speed, setSpeed] = useState(1.0);
  const [apiKey, setApiKey] = useState('');
  const [isKeyValid, setIsKeyValid] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [costEstimate, setCostEstimate] = useState(null);
  const [previewAudio, setPreviewAudio] = useState(null);
  const [generatedAudio, setGeneratedAudio] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  
  const audioRef = useRef(null);

  // Load voices on mount
  useEffect(() => {
    loadVoices();
  }, []);

  // Get cost estimate when manuscript changes
  useEffect(() => {
    if (manuscript && manuscript.length > 100) {
      estimateCost();
    }
  }, [manuscript]);

  const loadVoices = async () => {
    try {
      const response = await axios.get(`${API}/audiobook/voices`);
      setVoices(response.data.voices);
      
      // Set recommended voice based on writing type
      const recommendations = response.data.voice_recommendations[writingType] || 
                             response.data.voice_recommendations.default;
      if (recommendations && recommendations.length > 0) {
        setSelectedVoice(recommendations[0]);
      }
    } catch (err) {
      console.error('Error loading voices:', err);
    }
  };

  const estimateCost = async () => {
    try {
      const response = await axios.post(`${API}/audiobook/estimate`, {
        text: manuscript
      });
      setCostEstimate(response.data);
    } catch (err) {
      console.error('Error estimating cost:', err);
    }
  };

  const validateApiKey = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your API key');
      return;
    }
    
    setIsValidating(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('api_key', apiKey);
      
      const response = await axios.post(`${API}/audiobook/validate-key`, formData);
      
      setIsKeyValid(response.data.valid);
      if (!response.data.valid) {
        setError(response.data.message);
      }
    } catch (err) {
      setIsKeyValid(false);
      setError('Error validating key. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const generatePreview = async () => {
    if (!manuscript || manuscript.length < 10) {
      setError('Please add some text to your manuscript first');
      return;
    }
    
    setIsPreviewing(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/audiobook/preview`, {
        text: manuscript,
        voice: selectedVoice,
        speed: speed,
        api_key: apiKey || null
      });
      
      // Convert base64 to audio URL
      const audioUrl = `data:audio/mp3;base64,${response.data.audio_base64}`;
      setPreviewAudio(audioUrl);
      
      // Auto-play preview
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error generating preview');
    } finally {
      setIsPreviewing(false);
    }
  };

  const generateFullAudiobook = async () => {
    if (!manuscript || manuscript.length < 10) {
      setError('Please add some text to your manuscript first');
      return;
    }
    
    setIsGenerating(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/audiobook/generate-base64`, {
        text: manuscript,
        voice: selectedVoice,
        model: selectedModel,
        speed: speed,
        api_key: apiKey || null
      });
      
      // Convert base64 to downloadable blob
      const audioUrl = `data:audio/mp3;base64,${response.data.audio_base64}`;
      setGeneratedAudio({
        url: audioUrl,
        duration: response.data.estimated_duration,
        chunks: response.data.chunks_generated
      });
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Error generating audiobook');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadAudio = () => {
    if (!generatedAudio?.url) return;
    
    const link = document.createElement('a');
    link.href = generatedAudio.url;
    link.download = `audiobook_${selectedVoice}.mp3`;
    link.click();
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="audiobook-section" data-testid="audiobook-section">
      {/* Hidden audio element */}
      <audio 
        ref={audioRef} 
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
      />
      
      {/* API Key Section */}
      <div className="api-key-section">
        <div className="section-header">
          <h4><Key size={18} /> API Key Setup</h4>
          <button 
            className="help-btn"
            onClick={() => setShowGuide(true)}
            data-testid="api-key-help-btn"
          >
            <HelpCircle size={16} />
            <span>What's an API key?</span>
          </button>
        </div>
        
        <p className="section-description">
          To generate professional audiobooks, you'll need an OpenAI API key. 
          Don't have one? Click "What's an API key?" for easy setup instructions.
        </p>
        
        <div className="api-key-input-group">
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Paste your OpenAI API key here (sk-...)"
            className={`api-key-input ${isKeyValid === true ? 'valid' : ''} ${isKeyValid === false ? 'invalid' : ''}`}
            data-testid="api-key-input"
          />
          <button 
            className="validate-btn"
            onClick={validateApiKey}
            disabled={isValidating}
          >
            {isValidating ? <Loader2 className="spinning" size={16} /> : 'Validate'}
          </button>
        </div>
        
        {isKeyValid === true && (
          <div className="key-status valid">
            <CheckCircle size={16} /> API key is valid and ready to use!
          </div>
        )}
        
        {error && (
          <div className="key-status error">
            <AlertCircle size={16} /> {error}
          </div>
        )}
        
        <div className="key-note">
          <Info size={14} />
          <span>Without an API key, you can still use the free browser TTS for basic playback.</span>
        </div>
      </div>
      
      {/* Cost Estimate */}
      {costEstimate && (
        <div className="cost-estimate-card">
          <h4><DollarSign size={18} /> Estimated Cost for Your Manuscript</h4>
          <div className="estimate-grid">
            <div className="estimate-item">
              <span className="estimate-label">Characters</span>
              <span className="estimate-value">{costEstimate.character_count.toLocaleString()}</span>
            </div>
            <div className="estimate-item">
              <span className="estimate-label">Duration</span>
              <span className="estimate-value">{costEstimate.estimated_duration_formatted}</span>
            </div>
            <div className="estimate-item">
              <span className="estimate-label">Standard Quality</span>
              <span className="estimate-value">{costEstimate.cost_tts1}</span>
            </div>
            <div className="estimate-item highlight">
              <span className="estimate-label">HD Quality</span>
              <span className="estimate-value">{costEstimate.cost_tts1_hd}</span>
            </div>
          </div>
        </div>
      )}

      {/* Chapter Markers Section */}
      <ChapterMarkersSection 
        manuscript={manuscript} 
        onProcessedText={(processed) => {
          // Could update the manuscript with processed text if needed
          console.log('Chapters processed with markers');
        }}
      />
      
      {/* Voice Selection */}
      <div className="voice-selection">
        <h4><Volume2 size={18} /> Choose Your Narrator Voice</h4>
        <div className="voice-grid">
          {Object.entries(voices).map(([voiceId, voiceData]) => (
            <VoiceCard
              key={voiceId}
              voice={voiceId}
              voiceData={voiceData}
              selected={selectedVoice === voiceId}
              onSelect={setSelectedVoice}
            />
          ))}
        </div>
      </div>
      
      {/* Audio Settings */}
      <div className="audio-settings">
        <div className="setting-group">
          <label>Quality</label>
          <select 
            value={selectedModel} 
            onChange={(e) => setSelectedModel(e.target.value)}
            data-testid="quality-select"
          >
            <option value="tts-1">Standard (Faster, Lower Cost)</option>
            <option value="tts-1-hd">HD Quality (Recommended for Audiobooks)</option>
          </select>
        </div>
        
        <div className="setting-group">
          <label>Speed: {speed}x</label>
          <input
            type="range"
            min="0.5"
            max="2.0"
            step="0.1"
            value={speed}
            onChange={(e) => setSpeed(parseFloat(e.target.value))}
            className="speed-slider"
          />
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="audiobook-actions">
        <button 
          className="secondary-btn"
          onClick={generatePreview}
          disabled={isPreviewing || !manuscript}
          data-testid="preview-btn"
        >
          {isPreviewing ? (
            <>
              <Loader2 className="spinning" size={18} />
              <span>Generating Preview...</span>
            </>
          ) : (
            <>
              <Play size={18} />
              <span>Preview Voice</span>
            </>
          )}
        </button>
        
        <button 
          className="primary-btn"
          onClick={generateFullAudiobook}
          disabled={isGenerating || !manuscript}
          data-testid="generate-audiobook-btn"
        >
          {isGenerating ? (
            <>
              <Loader2 className="spinning" size={18} />
              <span>Generating Audiobook...</span>
            </>
          ) : (
            <>
              <Headphones size={18} />
              <span>Generate Full Audiobook</span>
            </>
          )}
        </button>
      </div>
      
      {/* Preview Player */}
      {previewAudio && (
        <div className="audio-player-card">
          <h4>Preview</h4>
          <div className="player-controls">
            <button className="play-btn" onClick={togglePlayPause}>
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>
            <span className="voice-label">Voice: {voices[selectedVoice]?.name}</span>
          </div>
        </div>
      )}
      
      {/* Generated Audio */}
      {generatedAudio && (
        <div className="generated-audio-card">
          <h4><CheckCircle size={18} /> Audiobook Generated!</h4>
          <p>Duration: {generatedAudio.duration} | Chunks: {generatedAudio.chunks}</p>
          <div className="audio-actions">
            <button 
              className="play-btn large"
              onClick={() => {
                if (audioRef.current) {
                  audioRef.current.src = generatedAudio.url;
                  audioRef.current.play();
                }
              }}
            >
              <Play size={24} />
              <span>Play</span>
            </button>
            <button className="download-btn large" onClick={downloadAudio}>
              <Download size={24} />
              <span>Download MP3</span>
            </button>
          </div>
        </div>
      )}
      
      {/* API Key Guide Modal */}
      <ApiKeyGuideModal isOpen={showGuide} onClose={() => setShowGuide(false)} />
    </div>
  );
};

export default AudiobookSection;
